/**
 * The component inventory.
 *
 * Small, accessible, token-driven. Everything a screen needs is here, so features
 * never hand-roll an input or reinvent a focus ring.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react'
import clsx from 'clsx'
import { useEscape } from './overlay'

/* ---------------------------------------------------------------- Button */

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'sm' | 'md'
  loading?: boolean
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={clsx(
        'inline-flex items-center justify-center gap-1.5 rounded-[var(--radius-control)] font-medium whitespace-nowrap transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-55',
        size === 'sm' ? 'h-7 px-2.5 text-[13px]' : 'h-8.5 px-3.5 text-sm',
        variant === 'primary' &&
          'bg-[var(--color-accent)] text-[var(--color-accent-contrast)] hover:brightness-110 active:brightness-95',
        variant === 'secondary' &&
          'border border-[var(--color-rule-strong)] bg-[var(--color-surface)] text-[var(--color-ink)] hover:bg-[var(--color-surface-sunk)]',
        variant === 'ghost' &&
          'text-[var(--color-ink-dim)] hover:bg-[var(--color-surface-sunk)] hover:text-[var(--color-ink)]',
        variant === 'danger' && 'bg-[var(--color-danger)] text-white hover:brightness-110',
        className,
      )}
    >
      {loading && <Spinner />}
      {children}
    </button>
  )
}

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={clsx(
        'inline-block size-3.5 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent',
        className,
      )}
    />
  )
}

/* ----------------------------------------------------------------- Field */

export function Field({
  label,
  hint,
  error,
  required,
  children,
}: {
  label: string
  hint?: string
  error?: string
  required?: boolean
  children: (props: {
    id: string
    'aria-invalid': boolean
    'aria-describedby'?: string
    'aria-required'?: boolean
  }) => ReactNode
}) {
  const id = useId()
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined
  return (
    <div>
      <label htmlFor={id} className="label">
        {label}
        {required && (
          <span aria-hidden className="ml-0.5 text-[var(--color-danger)]">
            *
          </span>
        )}
      </label>
      {children({
        id,
        'aria-invalid': Boolean(error),
        'aria-describedby': describedBy,
        'aria-required': required || undefined,
      })}
      {error ? (
        <p id={`${id}-error`} role="alert" className="mt-1 text-xs text-[var(--color-danger)]">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="mt-1 text-xs text-[var(--color-ink-faint)]">
          {hint}
        </p>
      ) : null}
    </div>
  )
}

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={clsx('input', props.className)} />
}

export function Textarea(
  props: React.TextareaHTMLAttributes<HTMLTextAreaElement>,
) {
  return <textarea {...props} className={clsx('input min-h-20 resize-y', props.className)} />
}

/* ------------------------------------------------------- Picker (combobox) */

export type Option = {
  value: string
  label: string
  hint?: string | null
  group?: string | null
  disabled?: boolean
}

/**
 * The entity picker. Every system reference in the product is one of these — never a
 * typed identifier. Type to filter, arrows to move, Enter to choose.
 */
export function Picker({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  clearable = true,
  disabled = false,
  id,
  'aria-invalid': invalid,
  'aria-describedby': describedBy,
  emptyText = 'No matches',
  openOnMount = false,
}: {
  options: Option[]
  value: string | null
  onChange: (value: string | null) => void
  placeholder?: string
  clearable?: boolean
  disabled?: boolean
  id?: string
  'aria-invalid'?: boolean
  'aria-describedby'?: string
  emptyText?: string
  /** Open the list straight away, for a picker that only exists because somebody has
   *  already clicked the cell it replaced. Without it a grid cell takes two clicks to
   *  edit: one to reveal a closed dropdown, and another to open it. */
  openOnMount?: boolean
}) {
  const [open, setOpen] = useState(openOnMount)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const boxRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listId = useId()

  const selected = options.find((o) => o.value === value) ?? null

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.hint ?? '').toLowerCase().includes(q) ||
        (o.group ?? '').toLowerCase().includes(q),
    )
  }, [options, query])

  useEffect(() => {
    if (!open) return
    const onDocClick = (event: MouseEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  useEffect(() => setActive(0), [query, open])

  const commit = useCallback(
    (option: Option | undefined) => {
      if (!option || option.disabled) return
      onChange(option.value)
      setOpen(false)
      setQuery('')
    },
    [onChange],
  )

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (!open) setOpen(true)
      else setActive((i) => Math.min(i + 1, filtered.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive((i) => Math.max(i - 1, 0))
    } else if (event.key === 'Enter') {
      if (open) {
        event.preventDefault()
        commit(filtered[active])
      }
    } else if (event.key === 'Escape') {
      // Handled here only when the list is actually open; otherwise it belongs to
      // whatever is underneath, which is usually the dialog.
      if (open) event.stopPropagation()
      setOpen(false)
      setQuery('')
    } else if (event.key === 'Backspace' && !query && clearable && selected) {
      onChange(null)
    }
  }

  return (
    <div ref={boxRef} className="relative">
      <div
        className={clsx(
          'input flex items-center gap-1.5',
          disabled && 'pointer-events-none opacity-60',
          open && 'ring-2 ring-[var(--color-focus)]/35',
        )}
      >
        <input
          ref={inputRef}
          id={id}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-invalid={invalid}
          aria-describedby={describedBy}
          autoComplete="off"
          disabled={disabled}
          value={open ? query : (selected?.label ?? '')}
          placeholder={selected ? selected.label : placeholder}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
          }}
          onKeyDown={onKeyDown}
          className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-[var(--color-ink-faint)]"
        />
        {clearable && selected && !disabled && (
          <button
            type="button"
            aria-label="Clear selection"
            onClick={() => {
              onChange(null)
              setQuery('')
              inputRef.current?.focus()
            }}
            className="shrink-0 rounded px-1 text-[var(--color-ink-faint)] hover:text-[var(--color-ink)]"
          >
            ×
          </button>
        )}
        <span aria-hidden className="shrink-0 text-[var(--color-ink-faint)]">
          ▾
        </span>
      </div>

      {open && (
        <ul
          id={listId}
          role="listbox"
          className="card absolute z-30 mt-1 max-h-64 w-full overflow-auto py-1 shadow-lg"
        >
          {filtered.length === 0 && (
            <li className="px-3 py-2 text-[13px] text-[var(--color-ink-faint)]">{emptyText}</li>
          )}
          {filtered.map((option, index) => {
            const showGroup =
              option.group && option.group !== filtered[index - 1]?.group
            return (
              <li key={option.value}>
                {showGroup && (
                  <div className="px-3 pt-2 pb-1 text-[10px] font-semibold tracking-wider text-[var(--color-ink-faint)] uppercase">
                    {option.group}
                  </div>
                )}
                <button
                  type="button"
                  role="option"
                  aria-selected={option.value === value}
                  disabled={option.disabled}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => commit(option)}
                  className={clsx(
                    'flex w-full items-baseline justify-between gap-3 px-3 py-1.5 text-left text-[13px]',
                    index === active && 'bg-[var(--color-accent-soft)]',
                    option.value === value && 'font-medium',
                    option.disabled && 'opacity-50',
                  )}
                >
                  {/* The label is what somebody is picking by; the hint only tells two
                      similar labels apart. So the hint gives up room first and truncates,
                      rather than squeezing a name down to "Ch…". */}
                  <span className="min-w-0 flex-1 truncate">{option.label}</span>
                  {option.hint && (
                    <span className="min-w-0 max-w-[50%] truncate text-[11px] text-[var(--color-ink-faint)]">
                      {option.hint}
                    </span>
                  )}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ Badge */

export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode
  tone?: 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info'
}) {
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium whitespace-nowrap',
        tone === 'neutral' && 'bg-[var(--color-surface-sunk)] text-[var(--color-ink-dim)]',
        tone === 'accent' && 'bg-[var(--color-accent-soft)] text-[var(--color-accent-ink)]',
        tone === 'success' && 'bg-[var(--color-success)]/15 text-[var(--color-success)]',
        tone === 'warning' && 'bg-[var(--color-warning)]/15 text-[var(--color-warning)]',
        tone === 'danger' && 'bg-[var(--color-danger)]/15 text-[var(--color-danger)]',
        tone === 'info' && 'bg-[var(--color-info)]/15 text-[var(--color-info)]',
      )}
    >
      {children}
    </span>
  )
}

export function StatusPill({ category, label }: { category: string; label: string }) {
  const tone = category === 'done' ? 'success' : category === 'in_progress' ? 'info' : 'neutral'
  return <Badge tone={tone}>{label}</Badge>
}

/* ------------------------------------------------------------------ Toast */

type Toast = { id: number; message: string; tone: 'info' | 'success' | 'danger' }
const ToastContext = createContext<(message: string, tone?: Toast['tone']) => void>(() => {})

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const notify = useCallback((message: string, tone: Toast['tone'] = 'info') => {
    const id = Date.now() + Math.random()
    setToasts((t) => [...t, { id, message, tone }])
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 5000)
  }, [])
  return (
    <ToastContext.Provider value={notify}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed right-4 bottom-4 z-50 flex flex-col gap-2"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={clsx(
              'card pointer-events-auto max-w-sm px-3.5 py-2.5 text-[13px] shadow-lg',
              toast.tone === 'danger' && 'border-[var(--color-danger)]',
              toast.tone === 'success' && 'border-[var(--color-success)]',
            )}
          >
            {toast.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast() {
  return useContext(ToastContext)
}

/* ----------------------------------------------------------------- Dialog */

export function Dialog({
  open,
  onClose,
  title,
  children,
  footer,
  width = 'md',
}: {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  footer?: ReactNode
  width?: 'sm' | 'md' | 'lg'
}) {
  useEscape(open, onClose)

  if (!open) return null
  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/35 p-4 pt-[10vh]">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={clsx(
          'card w-full shadow-2xl',
          width === 'sm' && 'max-w-sm',
          width === 'md' && 'max-w-lg',
          width === 'lg' && 'max-w-3xl',
        )}
      >
        <div className="flex items-center justify-between border-b border-[var(--color-rule)] px-4 py-3">
          <h2 className="text-[15px] font-semibold">{title}</h2>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
            ×
          </Button>
        </div>
        <div className="px-4 py-4">{children}</div>
        {footer && (
          <div className="flex justify-end gap-2 border-t border-[var(--color-rule)] px-4 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------ Empty state */

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string
  body?: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-14 text-center">
      <p className="text-[15px] font-medium">{title}</p>
      {body && <p className="max-w-sm text-[13px] text-[var(--color-ink-dim)]">{body}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  )
}

export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null
  const message = error instanceof Error ? error.message : String(error)
  return (
    <p role="alert" className="text-[13px] text-[var(--color-danger)]">
      {message}
    </p>
  )
}
